// VisaoGeral.tsx
//
// As contagens do cardápio que chegou na última carga e o resumo dos problemas
// encontrados. Todo número aqui é CONTADO no `decryptedInfoToken` — nenhum é
// cravado, nenhum é estimado.
import React from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/cardapioSection/index";
import {
  Cardapio,
  ProblemaCardapio,
  resumirCardapio,
} from "./regrasDeSaneamento";

interface Props {
  cardapio: Cardapio;
  problemas: ProblemaCardapio[];
}

const ROTULO_GRAVIDADE: Record<string, string> = {
  alta: "urgente",
  media: "atenção",
  baixa: "arrumação",
};

const VisaoGeral: React.FC<Props> = ({ cardapio, problemas }) => {
  const resumo = resumirCardapio(cardapio, problemas);
  const comOcorrencia = problemas.filter(
    (problema) => problema.registros.length > 0
  );
  const semOcorrencia = problemas.filter(
    (problema) => problema.registros.length === 0
  );

  if (resumo.categorias === 0 && resumo.produtos === 0) {
    return (
      <AlfredoStyles.EmptyState>
        <AlfredoStyles.EmptyStateIcon>🍽️</AlfredoStyles.EmptyStateIcon>
        <AlfredoStyles.EmptyStateTitle>
          Nenhum cardápio carregado
        </AlfredoStyles.EmptyStateTitle>
        <AlfredoStyles.EmptyStateDescription>
          O token desta sessão não traz categorias nem produtos. O cardápio
          chega na Central pela carga do OPS; enquanto ela não roda, não há o
          que mostrar aqui.
        </AlfredoStyles.EmptyStateDescription>
      </AlfredoStyles.EmptyState>
    );
  }

  return (
    <>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              restaurant_menu
            </AlfredoStyles.MaterialIcons>
            Visão geral do cardápio
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            O que a última carga do OPS trouxe para a Central
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      <AlfredoStyles.GradeIndicadores>
        <AlfredoStyles.Indicador>
          <AlfredoStyles.IndicadorNumero>
            {resumo.categorias}
          </AlfredoStyles.IndicadorNumero>
          <AlfredoStyles.IndicadorRotulo>Categorias</AlfredoStyles.IndicadorRotulo>
          <AlfredoStyles.IndicadorNota>
            São as categorias do cardápio, que carregam o preço. Não confundir
            com as categorias de insumo, que ficam em Suprimentos.
          </AlfredoStyles.IndicadorNota>
        </AlfredoStyles.Indicador>

        <AlfredoStyles.Indicador>
          <AlfredoStyles.IndicadorNumero>
            {resumo.produtos}
          </AlfredoStyles.IndicadorNumero>
          <AlfredoStyles.IndicadorRotulo>Produtos</AlfredoStyles.IndicadorRotulo>
          <AlfredoStyles.IndicadorNota>
            Cadastros de produto da rede. O cadastro não guarda preço nem
            unidade — só código, nome e as marcas de combo e taxa de serviço.
          </AlfredoStyles.IndicadorNota>
        </AlfredoStyles.Indicador>

        <AlfredoStyles.Indicador>
          <AlfredoStyles.IndicadorNumero>
            {resumo.produtosComPreco}
          </AlfredoStyles.IndicadorNumero>
          <AlfredoStyles.IndicadorRotulo>
            Produtos com preço
          </AlfredoStyles.IndicadorRotulo>
          <AlfredoStyles.IndicadorNota>
            O preço só existe dentro da categoria. Um produto tem preço quando
            aparece em alguma.
          </AlfredoStyles.IndicadorNota>
        </AlfredoStyles.Indicador>

        <AlfredoStyles.Indicador>
          <AlfredoStyles.IndicadorNumero
            $tom={resumo.produtosSemCategoria > 0 ? "alerta" : "neutro"}
          >
            {resumo.produtosSemCategoria}
          </AlfredoStyles.IndicadorNumero>
          <AlfredoStyles.IndicadorRotulo>
            Produtos sem categoria
          </AlfredoStyles.IndicadorRotulo>
          <AlfredoStyles.IndicadorNota>
            Cadastrados, sem preço e fora do cardápio do app — mas ainda
            ocupando código.
          </AlfredoStyles.IndicadorNota>
        </AlfredoStyles.Indicador>

        <AlfredoStyles.Indicador>
          <AlfredoStyles.IndicadorNumero>
            {resumo.produtosCombo}
          </AlfredoStyles.IndicadorNumero>
          <AlfredoStyles.IndicadorRotulo>
            Marcados como combo
          </AlfredoStyles.IndicadorRotulo>
          <AlfredoStyles.IndicadorNota>
            É só a marca. O que compõe cada combo não vem no dado da Central —
            ver a sub-tela Combos.
          </AlfredoStyles.IndicadorNota>
        </AlfredoStyles.Indicador>

        <AlfredoStyles.Indicador>
          <AlfredoStyles.IndicadorNumero
            $tom={resumo.registrosComProblema > 0 ? "alerta" : "neutro"}
          >
            {resumo.registrosComProblema}
          </AlfredoStyles.IndicadorNumero>
          <AlfredoStyles.IndicadorRotulo>
            Registros com algum problema
          </AlfredoStyles.IndicadorRotulo>
          <AlfredoStyles.IndicadorNota>
            Em {resumo.problemasComOcorrencia} de {problemas.length} verificações.
            O mesmo produto pode aparecer em mais de uma.
          </AlfredoStyles.IndicadorNota>
        </AlfredoStyles.Indicador>
      </AlfredoStyles.GradeIndicadores>

      {(resumo.categoriasIlegiveis > 0 || resumo.citadosSemCadastro > 0) && (
        <AlfredoStyles.Aviso $tom="atencao">
          {resumo.categoriasIlegiveis > 0 && (
            <div>
              {resumo.categoriasIlegiveis} categoria(s) vieram com o valor
              gravado ilegível — a lista de produtos delas não pôde ser lida, e
              os produtos dessas categorias não entram em nenhuma contagem desta
              tela.
            </div>
          )}
          {resumo.citadosSemCadastro > 0 && (
            <div>
              {resumo.citadosSemCadastro} produto(s) são citados dentro de uma
              categoria sem ter cadastro de produto na rede. O PDV mostra o
              item, mas não há registro por trás dele.
            </div>
          )}
        </AlfredoStyles.Aviso>
      )}

      <AlfredoStyles.SecaoTitulo>
        <AlfredoStyles.MaterialIcons>fact_check</AlfredoStyles.MaterialIcons>
        Resumo do saneamento
      </AlfredoStyles.SecaoTitulo>

      {comOcorrencia.length === 0 ? (
        <AlfredoStyles.Aviso $tom="ok">
          Nenhuma das {problemas.length} verificações encontrou ocorrência neste
          cardápio.
        </AlfredoStyles.Aviso>
      ) : (
        <AlfredoStyles.Cartao>
          <AlfredoStyles.CartaoTexto>
            Cada linha é uma verificação que encontrou ocorrência. A lista
            nominal — com código e nome de cada registro — está em Saneamento.
          </AlfredoStyles.CartaoTexto>
          <AlfredoStyles.ListaRegistros>
            {comOcorrencia.map((problema) => (
              <AlfredoStyles.RegistroLinha key={problema.chave}>
                <AlfredoStyles.MaterialIcons
                  style={{ fontSize: "var(--fs-base)" }}
                >
                  {problema.icone}
                </AlfredoStyles.MaterialIcons>
                <AlfredoStyles.RegistroNome>
                  {problema.registros.length} — {problema.titulo}
                </AlfredoStyles.RegistroNome>
                <AlfredoStyles.Etiqueta $tom={problema.gravidade}>
                  {ROTULO_GRAVIDADE[problema.gravidade]}
                </AlfredoStyles.Etiqueta>
                <AlfredoStyles.RegistroDetalhe>
                  {problema.oQueQuebra}
                </AlfredoStyles.RegistroDetalhe>
              </AlfredoStyles.RegistroLinha>
            ))}
          </AlfredoStyles.ListaRegistros>
        </AlfredoStyles.Cartao>
      )}

      {semOcorrencia.length > 0 && (
        <AlfredoStyles.Cartao>
          <AlfredoStyles.CartaoTitulo>
            <AlfredoStyles.MaterialIcons>done_all</AlfredoStyles.MaterialIcons>
            Verificações sem ocorrência
          </AlfredoStyles.CartaoTitulo>
          <AlfredoStyles.CartaoTexto>
            Rodaram e não acharam nada neste cardápio. Ficam listadas porque
            "não apareceu" é diferente de "não foi verificado".
          </AlfredoStyles.CartaoTexto>
          <AlfredoStyles.ListaRegistros>
            {semOcorrencia.map((problema) => (
              <AlfredoStyles.RegistroLinha key={problema.chave}>
                <AlfredoStyles.Etiqueta $tom="ok">0</AlfredoStyles.Etiqueta>
                <AlfredoStyles.RegistroNome>
                  {problema.titulo}
                </AlfredoStyles.RegistroNome>
              </AlfredoStyles.RegistroLinha>
            ))}
          </AlfredoStyles.ListaRegistros>
        </AlfredoStyles.Cartao>
      )}
    </>
  );
};

export default VisaoGeral;
